import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Same palette as heatPdf.js so the two documents look like one family.
const NAVY  = [26, 26, 46];
const GOLD  = [180, 130, 20];
const GREY  = [100, 100, 110];
const LGREY = [160, 160, 170];

/** Two-tone "SCALA Song Competition" wordmark, as used on the heat documents. */
function drawScalaLogo(doc, x, y, scalaPt = 18, tagPt = 11) {
  doc.setFontSize(scalaPt);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GOLD);
  doc.text('SCALA', x, y);
  const scalaW = doc.getTextWidth('SCALA');

  doc.setFontSize(tagPt);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...NAVY);
  doc.text(' Song Competition', x + scalaW, y);
}

/**
 * Placement labels for one song, as shown on screen: the organiser's award
 * first, then each judge or live-heat award with a count where more than one
 * gave it. Takes the already-computed list so the PDF cannot disagree with the
 * panel it is printed from.
 */
function placementLabels(song, placements) {
  const parts = [];
  if (song.admin_placement_name) parts.push(`${song.admin_placement_name} (organiser)`);
  for (const pl of placements) parts.push(pl.n > 1 ? `${pl.name} x${pl.n}` : pl.name);
  return parts.join(', ') || '—';
}

/**
 * Generate and download a PDF of the "Most celebrated" panel.
 *
 * @param {Array}  groups        [{ category, songs }] in display order
 * @param {Object} opts
 * @param {Function} opts.placementsFor  song -> [{ name, n, order }]
 * @param {number}   opts.year           competition year, for the title
 */
export function downloadCelebratedPdf(groups, { placementsFor, year, onStart, onDone } = {}) {
  onStart?.();

  const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const pageH  = doc.internal.pageSize.getHeight();
  const margin = 15;

  // ── Header ────────────────────────────────────────────────────────────────
  let y = margin;
  drawScalaLogo(doc, margin, y);
  y += 10;

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  doc.text('Most Celebrated', margin, y);
  y += 6;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GREY);
  doc.text(`Festival Of Original Music ${year || ''}`.trim(), margin, y);
  y += 5;

  const generated = new Date().toLocaleString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  doc.setFontSize(8);
  doc.setTextColor(...LGREY);
  doc.text(`Generated ${generated}`, margin, y);
  y += 3;

  // Judging is often still under way when this is printed, so the document says
  // so rather than reading as a final result.
  doc.text('Placements as recorded at the time of printing.', margin, y);
  y += 6;

  const total = groups.reduce((n, g) => n + g.songs.length, 0);
  if (total === 0) {
    doc.setFontSize(11);
    doc.setTextColor(...GREY);
    doc.text('No placements have been awarded yet.', margin, y + 4);
    doc.save(`FOOM-${year || ''}-Most-Celebrated.pdf`.replace('--', '-'));
    onDone?.();
    return;
  }

  // ── One table per category ────────────────────────────────────────────────
  for (const { category, songs } of groups) {
    // Keep a heading with at least the start of its table.
    if (y > pageH - 45) { doc.addPage(); y = margin; }

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...NAVY);
    doc.text(`${category}  (${songs.length})`, margin, y);
    y += 3;

    if (songs.length === 0) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...LGREY);
      doc.text('No placements yet.', margin, y + 4);
      doc.setFont('helvetica', 'normal');
      y += 10;
      continue;
    }

    let firstPageOfTable = true;
    autoTable(doc, {
      startY: y + 1,
      margin: { left: margin, right: margin, top: margin + 8 },
      // A category can outgrow the page. autoTable repeats the column headers
      // but not our heading, so a continuation page would otherwise show a
      // table with no indication of which category it belongs to.
      didDrawPage: () => {
        if (firstPageOfTable) { firstPageOfTable = false; return; }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...NAVY);
        doc.text(`${category} (continued)`, margin, margin + 3);
        doc.setFont('helvetica', 'normal');
      },
      head: [['Song', 'Contestant', 'Placement']],
      body: songs.map((s) => [
        s.title || '',
        s.contestant_name || '',
        placementLabels(s, placementsFor ? placementsFor(s) : []),
      ]),
      styles:     { fontSize: 9, cellPadding: 2, textColor: NAVY, lineColor: [225, 227, 232], lineWidth: 0.1 },
      headStyles: { fillColor: [245, 246, 248], textColor: GREY, fontStyle: 'bold', fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 62 },
        1: { cellWidth: 45 },
        2: { cellWidth: 'auto' },
      },
      theme: 'grid',
    });

    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Page numbers ──────────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...LGREY);
    doc.text(`Page ${i} of ${pages}`, pageW - margin, pageH - 8, { align: 'right' });
  }

  doc.save(`FOOM-${year || ''}-Most-Celebrated.pdf`.replace('--', '-'));
  onDone?.();
}
